import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { 
  ArrowLeft, 
  ArrowRight, 
  Calendar, 
  Clock, 
  Repeat, 
  Send
} from 'lucide-react';
import { usePostingStore, PlatformId, ScheduleMode } from '../../lib/store/usePostingStore';

const Step4SchedulePreview: React.FC = () => {
  const { 
    files, 
    platforms, 
    captions, 
    titles,
    descriptions,
    thumbnails,
    schedule,
    setScheduleMode,
    setScheduleDateTime,
    setScheduleTimezone,
    nextStep, 
    prevStep 
  } = usePostingStore();
  
  const [previewPlatform, setPreviewPlatform] = useState<PlatformId | null>(
    platforms.find(p => p.enabled)?.id || null
  );
  
  const enabledPlatforms = platforms.filter(p => p.enabled);
  
  // If no platforms are enabled, redirect back to step 1
  if (enabledPlatforms.length === 0) {
    prevStep();
    return null;
  }
  
  const handleScheduleModeChange = (mode: ScheduleMode) => {
    setScheduleMode(mode);
    
    // If switching to 'once' mode, set default date to tomorrow
    if (mode === 'once' && !schedule.datetime) {
      const tomorrow = new Date();
      tomorrow.setDate(tomorrow.getDate() + 1);
      tomorrow.setHours(9, 0, 0, 0);
      setScheduleDateTime(tomorrow);
    }
  };
  
  const handleDateChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newDate = new Date(e.target.value);
    
    // Check if schedule.datetime is a valid Date object
    if (schedule.datetime instanceof Date) {
      // Preserve the time from the existing datetime
      newDate.setHours(
        schedule.datetime.getHours(),
        schedule.datetime.getMinutes(),
        0,
        0
      );
    } else {
      // Default to 9:00 AM
      newDate.setHours(9, 0, 0, 0);
    }
    
    setScheduleDateTime(newDate);
  };
  
  const handleTimeChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const [hours, minutes] = e.target.value.split(':').map(Number);
    
    // Create a new date object, ensuring we have a valid Date
    const newDate = schedule.datetime instanceof Date 
      ? new Date(schedule.datetime) 
      : new Date();
      
    newDate.setHours(hours, minutes, 0, 0);
    
    setScheduleDateTime(newDate);
  };
  
  const formatDate = (date: Date) => {
    return date.toISOString().split('T')[0];
  };
  
  const formatTime = (date: Date) => {
    return `${date.getHours().toString().padStart(2, '0')}:${date.getMinutes().toString().padStart(2, '0')}`;
  };
  
  const getFilePreview = (file: File) => {
    if (file.type.startsWith('image/')) {
      return (
        <div className="relative w-full h-40 bg-gray-100 dark:bg-dark-700 rounded-lg overflow-hidden">
          <img
            src={URL.createObjectURL(file)}
            alt="Preview"
            className="w-full h-full object-cover"
          />
        </div>
      );
    } else if (file.type.startsWith('video/')) {
      return (
        <div className="relative w-full h-40 bg-gray-100 dark:bg-dark-700 rounded-lg overflow-hidden">
          <video
            src={URL.createObjectURL(file)}
            className="w-full h-full object-cover"
            controls
          />
        </div>
      );
    } else {
      return (
        <div className="flex items-center justify-center w-full h-40 bg-gray-100 dark:bg-dark-700 rounded-lg">
          <File className="h-12 w-12 text-gray-400" />
        </div>
      );
    }
  };
  
  const renderPlatformPreview = (platformId: PlatformId) => {
    const platform = platforms.find(p => p.id === platformId);
    if (!platform) return null;
    
    const caption = captions[platformId] || '';
    const title = titles[platformId] || '';
    const description = descriptions[platformId] || '';
    const thumbnail = thumbnails[platformId];
    
    return (
      <div className="bg-white dark:bg-dark-800 rounded-lg border border-gray-200 dark:border-dark-700 overflow-hidden">
        <div className="p-4 border-b border-gray-200 dark:border-dark-700">
          <div className="flex items-center space-x-3">
            <div className="w-10 h-10 rounded-full bg-gray-200 dark:bg-dark-700 flex items-center justify-center">
              <span className="text-gray-500 dark:text-gray-400 font-medium">YA</span>
            </div>
            <div>
              <p className="text-sm font-medium text-gray-900 dark:text-white">Your Account</p>
              <p className="text-xs text-gray-500 dark:text-gray-400">@youraccount</p>
            </div>
          </div>
        </div>
        
        <div className="p-4">
          {/* Media Preview */}
          {files.length > 0 && getFilePreview(files[0])}
          
          {/* YouTube specific */}
          {platformId === 'yt' && (
            <div className="mt-4">
              <h3 className="text-base font-medium text-gray-900 dark:text-white">{title || 'Video Title'}</h3>
              <p className="mt-1 text-sm text-gray-600 dark:text-gray-400 line-clamp-3">
                {description || 'Video description...'}
              </p>
            </div>
          )}
          
          {/* Caption */}
          <p className="mt-4 text-sm text-gray-900 dark:text-white">
            {caption || `Your ${platform.name} caption will appear here...`}
          </p>
        </div>
        
        <div className="px-4 py-3 bg-gray-50 dark:bg-dark-700 text-xs text-gray-500 dark:text-gray-400">
          {schedule.mode === 'now' ? (
            'Will be posted immediately'
          ) : schedule.mode === 'once' && schedule.datetime instanceof Date ? (
            `Scheduled for ${schedule.datetime.toLocaleString()}`
          ) : (
            'Recurring schedule'
          )}
        </div>
      </div>
    );
  };
  
  return (
    <div className="space-y-8">
      {/* Schedule Options */}
      <div className="space-y-6 schedule-options">
        <h3 className="text-lg font-medium text-gray-900 dark:text-white">
          When to Publish
        </h3>
        
        <div className="space-y-4">
          <div className="flex flex-col sm:flex-row space-y-3 sm:space-y-0 sm:space-x-4">
            <button
              onClick={() => handleScheduleModeChange('now')}
              className={`flex-1 flex items-center justify-center px-4 py-3 rounded-lg border-2 transition-colors ${
                schedule.mode === 'now'
                  ? 'border-indigo-500 bg-indigo-50 dark:bg-indigo-900/20'
                  : 'border-gray-200 dark:border-dark-700 hover:border-indigo-300 dark:hover:border-indigo-700'
              }`}
            >
              <Send className="h-5 w-5 mr-2 text-indigo-600 dark:text-indigo-400" />
              <span className="font-medium text-gray-900 dark:text-white">Post Now</span>
            </button>
            
            <button
              onClick={() => handleScheduleModeChange('once')}
              className={`flex-1 flex items-center justify-center px-4 py-3 rounded-lg border-2 transition-colors ${
                schedule.mode === 'once'
                  ? 'border-indigo-500 bg-indigo-50 dark:bg-indigo-900/20'
                  : 'border-gray-200 dark:border-dark-700 hover:border-indigo-300 dark:hover:border-indigo-700'
              }`}
            >
              <Calendar className="h-5 w-5 mr-2 text-indigo-600 dark:text-indigo-400" />
              <span className="font-medium text-gray-900 dark:text-white">Schedule Once</span>
            </button>
            
            <button
              onClick={() => handleScheduleModeChange('recurring')}
              className={`flex-1 flex items-center justify-center px-4 py-3 rounded-lg border-2 transition-colors ${
                schedule.mode === 'recurring'
                  ? 'border-indigo-500 bg-indigo-50 dark:bg-indigo-900/20'
                  : 'border-gray-200 dark:border-dark-700 hover:border-indigo-300 dark:hover:border-indigo-700'
              }`}
            >
              <Repeat className="h-5 w-5 mr-2 text-indigo-600 dark:text-indigo-400" />
              <span className="font-medium text-gray-900 dark:text-white">Recurring</span>
            </button>
          </div>
          
          {/* Date and Time Pickers for Scheduled Posts */}
          {schedule.mode === 'once' && (
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Date
                </label>
                <div className="relative">
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                    <Calendar className="h-5 w-5 text-gray-400" />
                  </div>
                  <input
                    type="date"
                    value={schedule.datetime && schedule.datetime instanceof Date ? formatDate(schedule.datetime) : ''}
                    onChange={handleDateChange}
                    min={formatDate(new Date())}
                    className="block w-full pl-10 pr-3 py-2 border border-gray-300 dark:border-dark-600 rounded-lg focus:ring-indigo-500 focus:border-indigo-500 dark:bg-dark-700 dark:text-white"
                  />
                </div>
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Time
                </label>
                <div className="relative">
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                    <Clock className="h-5 w-5 text-gray-400" />
                  </div>
                  <input
                    type="time"
                    value={schedule.datetime && schedule.datetime instanceof Date ? formatTime(schedule.datetime) : ''}
                    onChange={handleTimeChange}
                    className="block w-full pl-10 pr-3 py-2 border border-gray-300 dark:border-dark-600 rounded-lg focus:ring-indigo-500 focus:border-indigo-500 dark:bg-dark-700 dark:text-white"
                  />
                </div>
              </div>
            </div>
          )}
          
          {/* Recurring Schedule Options */}
          {schedule.mode === 'recurring' && (
            <div className="p-4 bg-indigo-50 dark:bg-indigo-900/20 rounded-lg">
              <p className="text-sm text-indigo-700 dark:text-indigo-300">
                Recurring schedule options will be available in a future update.
              </p>
            </div>
          )}
          
          {/* Timezone Selector */}
          {schedule.mode !== 'now' && (
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Timezone
              </label>
              <select
                value={schedule.timezone}
                onChange={(e) => setScheduleTimezone(e.target.value)}
                className="block w-full px-3 py-2 border border-gray-300 dark:border-dark-600 rounded-lg focus:ring-indigo-500 focus:border-indigo-500 dark:bg-dark-700 dark:text-white"
              >
                <option value="America/New_York">Eastern Time (ET)</option>
                <option value="America/Chicago">Central Time (CT)</option>
                <option value="America/Denver">Mountain Time (MT)</option>
                <option value="America/Los_Angeles">Pacific Time (PT)</option>
                <option value="UTC">UTC</option>
              </select>
            </div>
          )}
        </div>
      </div>
      
      {/* Platform Preview */}
      <div className="space-y-6">
        <div className="flex items-center justify-between">
          <h3 className="text-lg font-medium text-gray-900 dark:text-white">
            Preview
          </h3>
          <div className="flex space-x-2">
            {enabledPlatforms.map((platform) => (
              <button
                key={platform.id}
                onClick={() => setPreviewPlatform(platform.id)}
                className={`p-2 rounded-full transition-colors ${
                  previewPlatform === platform.id
                    ? 'bg-indigo-100 dark:bg-indigo-900/30 text-indigo-600 dark:text-indigo-400'
                    : 'bg-gray-100 dark:bg-dark-700 text-gray-500 dark:text-gray-400 hover:bg-gray-200 dark:hover:bg-dark-600'
                }`}
              >
                {platform.id === 'fb' && <FacebookIcon className="h-5 w-5" />}
                {platform.id === 'ig' && <InstagramIcon className="h-5 w-5" />}
                {platform.id === 'yt' && <YoutubeIcon className="h-5 w-5" />}
                {platform.id === 'ln' && <LinkedinIcon className="h-5 w-5" />}
                {platform.id === 'x' && <TwitterIcon className="h-5 w-5" />}
                {platform.id === 'tt' && <TikTokIcon className="h-5 w-5" />}
              </button>
            ))}
          </div>
        </div>
        
        {previewPlatform && renderPlatformPreview(previewPlatform)}
      </div>
      
      {/* Navigation Buttons */}
      <div className="flex justify-between pt-6">
        <button
          onClick={prevStep}
          className="px-4 py-2 border border-gray-300 dark:border-dark-600 text-gray-700 dark:text-gray-300 rounded-lg hover:bg-gray-50 dark:hover:bg-dark-700 transition-colors"
        >
          <div className="flex items-center">
            <ArrowLeft className="h-4 w-4 mr-2" />
            Back
          </div>
        </button>
        <button
          onClick={nextStep}
          className="px-6 py-3 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition-colors"
        >
          Continue to Review
        </button>
      </div>
    </div>
  );
};

// Import the necessary icons
const FacebookIcon = (props: any) => (
  <svg viewBox="0 0 24 24" fill="currentColor" {...props}>
    <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z" />
  </svg>
);

const InstagramIcon = (props: any) => (
  <svg viewBox="0 0 24 24" fill="currentColor" {...props}>
    <path d="M12 0C8.74 0 8.333.015 7.053.072 5.775.132 4.905.333 4.14.63c-.789.306-1.459.717-2.126 1.384S.935 3.35.63 4.14C.333 4.905.131 5.775.072 7.053.012 8.333 0 8.74 0 12s.015 3.667.072 4.947c.06 1.277.261 2.148.558 2.913.306.788.717 1.459 1.384 2.126.667.666 1.336 1.079 2.126 1.384.766.296 1.636.499 2.913.558C8.333 23.988 8.74 24 12 24s3.667-.015 4.947-.072c1.277-.06 2.148-.262 2.913-.558.788-.306 1.459-.718 2.126-1.384.666-.667 1.079-1.335 1.384-2.126.296-.765.499-1.636.558-2.913.06-1.28.072-1.687.072-4.947s-.015-3.667-.072-4.947c-.06-1.277-.262-2.149-.558-2.913-.306-.789-.718-1.459-1.384-2.126C21.319 1.347 20.651.935 19.86.63c-.765-.297-1.636-.499-2.913-.558C15.667.012 15.26 0 12 0zm0 2.16c3.203 0 3.585.016 4.85.071 1.17.055 1.805.249 2.227.415.562.217.96.477 1.382.896.419.42.679.819.896 1.381.164.422.36 1.057.413 2.227.057 1.266.07 1.646.07 4.85s-.015 3.585-.074 4.85c-.061 1.17-.256 1.805-.421 2.227-.224.562-.479.96-.899 1.382-.419.419-.824.679-1.38.896-.42.164-1.065.36-2.235.413-1.274.057-1.649.07-4.859.07-3.211 0-3.586-.015-4.859-.074-1.171-.061-1.816-.256-2.236-.421-.569-.224-.96-.479-1.379-.899-.421-.419-.69-.824-.9-1.38-.165-.42-.359-1.065-.42-2.235-.045-1.26-.061-1.649-.061-4.844 0-3.196.016-3.586.061-4.861.061-1.17.255-1.814.42-2.234.21-.57.479-.96.9-1.381.419-.419.81-.689 1.379-.898.42-.166 1.051-.361 2.221-.421 1.275-.045 1.65-.06 4.859-.06l.045.03zm0 3.678c-3.405 0-6.162 2.76-6.162 6.162 0 3.405 2.76 6.162 6.162 6.162 3.405 0 6.162-2.76 6.162-6.162 0-3.405-2.76-6.162-6.162-6.162zM12 16c-2.21 0-4-1.79-4-4s1.79-4 4-4 4 1.79 4 4-1.79 4-4 4zm7.846-10.405c0 .795-.646 1.44-1.44 1.44-.795 0-1.44-.646-1.44-1.44 0-.794.646-1.439 1.44-1.439.793-.001 1.44.645 1.44 1.439z" />
  </svg>
);

const TwitterIcon = (props: any) => (
  <svg viewBox="0 0 24 24" fill="currentColor" {...props}>
    <path d="M23.953 4.57a10 10 0 01-2.825.775 4.958 4.958 0 002.163-2.723c-.951.555-2.005.959-3.127 1.184a4.92 4.92 0 00-8.384 4.482C7.69 8.095 4.067 6.13 1.64 3.162a4.822 4.822 0 00-.666 2.475c0 1.71.87 3.213 2.188 4.096a4.904 4.904 0 01-2.228-.616v.06a4.923 4.923 0 003.946 4.827 4.996 4.996 0 01-2.212.085 4.936 4.936 0 004.604 3.417 9.867 9.867 0 01-6.102 2.105c-.39 0-.779-.023-1.17-.067a13.995 13.995 0 007.557 2.209c9.053 0 13.998-7.496 13.998-13.985 0-.21 0-.42-.015-.63A9.935 9.935 0 0024 4.59z" />
  </svg>
);

const LinkedinIcon = (props: any) => (
  <svg viewBox="0 0 24 24" fill="currentColor" {...props}>
    <path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433c-1.144 0-2.063-.926-2.063-2.065 0-1.138.92-2.063 2.063-2.063 1.14 0 2.064.925 2.064 2.063 0 1.139-.925 2.065-2.064 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z" />
  </svg>
);

const YoutubeIcon = (props: any) => (
  <svg viewBox="0 0 24 24" fill="currentColor" {...props}>
    <path d="M23.498 6.186a3.016 3.016 0 0 0-2.122-2.136C19.505 3.545 12 3.545 12 3.545s-7.505 0-9.377.505A3.017 3.017 0 0 0 .502 6.186C0 8.07 0 12 0 12s0 3.93.502 5.814a3.016 3.016 0 0 0 2.122 2.136c1.871.505 9.376.505 9.376.505s7.505 0 9.377-.505a3.015 3.015 0 0 0 2.122-2.136C24 15.93 24 12 24 12s0-3.93-.502-5.814zM9.545 15.568V8.432L15.818 12l-6.273 3.568z" />
  </svg>
);

const TikTokIcon = (props: any) => (
  <svg viewBox="0 0 24 24" fill="currentColor" {...props}>
    <path d="M12.525.02c1.31-.02 2.61-.01 3.91-.02.08 1.53.63 3.09 1.75 4.17 1.12 1.11 2.7 1.62 4.24 1.79v4.03c-1.44-.05-2.89-.35-4.2-.97-.57-.26-1.1-.59-1.62-.93-.01 2.92.01 5.84-.02 8.75-.08 1.4-.54 2.79-1.35 3.94-1.31 1.92-3.58 3.17-5.91 3.21-1.43.08-2.86-.31-4.08-1.03-2.02-1.19-3.44-3.37-3.65-5.71-.02-.5-.03-1-.01-1.49.18-1.9 1.12-3.72 2.58-4.96 1.66-1.44 3.98-2.13 6.15-1.72.02 1.48-.04 2.96-.04 4.44-.99-.32-2.15-.23-3.02.37-.63.41-1.11 1.04-1.36 1.75-.21.51-.15 1.07-.14 1.61.24 1.64 1.82 3.02 3.5 2.87 1.12-.01 2.19-.66 2.77-1.61.19-.33.4-.67.41-1.06.1-1.79.06-3.57.07-5.36.01-4.03-.01-8.05.02-12.07z" />
  </svg>
);

export default Step4SchedulePreview;